import React, { useEffect, useState } from 'react';
import axios from "@/lib/axios";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { API_BASE_URL } from '@/config';
import { useLoader } from "@/contexts/LoaderContext";
import { useJourney } from "@/contexts/JourneyContext";
import CallbackHelp from "@/components/CallbackHelp";
import { stringOrEmpty, stringMandatory, gstString } from "@/helpers/validations";
import JourneyHeader from "@/components/JourneyHeader";

export const businessFormLoadSchema = z.object({
  business_name: stringOrEmpty("Business Name"),
  business_role: stringOrEmpty("Role"),
  other_role: stringOrEmpty("Other Role"),
  gst_number: stringOrEmpty("GST Number"),
  business_constitution_type: stringOrEmpty("Constitution"),
  business_vintage: stringOrEmpty("Business Vintage"),
  business_premise_ownership: stringOrEmpty("Premise Ownership"),
  agent_code: z.string().trim().optional(),
});

export const businessFormSubmitSchema = z.object({
  business_name: stringMandatory("Business Name"),
  business_role: stringMandatory("Role"),
  other_role: z.string().trim().optional(),
  gst_number: gstString("GST Number"),
  business_constitution_type: stringMandatory("Constitution"),
  business_vintage: stringMandatory("Business Vintage"),
  business_premise_ownership: stringMandatory("Premise Ownership"),
  agent_code: z.string().trim().optional(),
}).refine(
  (data) => {
    if (data.business_role === "other" && !data.other_role?.trim()) return false;
    return true;
  },
  {
    message: "Specify Role when 'Other' is selected",
    path: ["other_role"],
  }
);

type FormValues = z.infer<typeof businessFormSubmitSchema>;

const MANDATORY_GST_BAPS = ["GoKwik","Easebuzz"];

const BusinessInfoWhitespace = () => {
  const navigate = useNavigate();
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const { loading, setLoading } = useLoader();
  const { refresh: refreshJourney } = useJourney();
  const [isBusinessNameEditable, setIsBusinessNameEditable] = useState(false);
  const sessionBapId = sessionStorage.getItem('whitespace_bap_id');
  const [agents, setAgents] = useState<string[]>([]);
  const [openAgent, setOpenAgent] = useState(false);



  const form = useForm<FormValues>({
    resolver: zodResolver(businessFormSubmitSchema),
    mode: "onTouched",
    shouldUnregister: true,
    defaultValues: {
      business_name: "",
      business_role: "",
      other_role: "",
      gst_number: "",
      business_constitution_type: "",
      business_vintage: "",
      business_premise_ownership: "",
      agent_code: "",
    },
  });

  const business_role = form.watch("business_role");

  useEffect(() => {
    if (business_role !== "other") {
      form.setValue("other_role", "");
      form.clearErrors("other_role");
    } else {
      form.trigger("other_role");
    }
  }, [business_role, form]);

  const onSubmit = (data: FormValues) => {
    setLoading(true);
    axios.post(`${API_BASE_URL}/api/set_business_details/`, data, { maxRedirects: 0 })
      .then((res) => {
        if (res.status === 200) {
          refreshJourney().then(() => {
            setTimeout(() => {
              navigate(res.data.next_page_url || "/loan-amount");
            }, 150);
          }).catch(() => {
            setTimeout(() => {
              navigate(res.data.next_page_url || "/loan-amount");
            }, 150);
          });
        } else if (res.request.responseURL.includes("login/")) {
          navigate("/session-timeout");
        }
      }).catch((err) => {
        console.error("Error saving data:", err);
      }).finally(() => setLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    const fetchBusinessDetails = axios.post(`${API_BASE_URL}/api/get_business_details/`, {}, { maxRedirects: 0 });
    const fetchAgents = axios.post(`${API_BASE_URL}/api/get_agents/`);

    Promise.all([fetchBusinessDetails, fetchAgents])
      .then(async ([resBiz, resAgents]) => {
        if (resBiz.request.responseURL.includes("login/")) {
          navigate("/session-timeout");
        }
        if (!resBiz.data.business_name || resBiz.data.business_name === 'WHITESPACE') {
          setIsBusinessNameEditable(true);
        }

        // Set agents first as form reset might depend on it for field visibility
        setAgents(resAgents.data.agents || []);

        const loadResult = businessFormLoadSchema.safeParse(resBiz.data);
        if (loadResult.success) {
          form.reset(loadResult.data);
          // Default agent_code to PP_1 for PetPooja if not already set
          if (sessionBapId === "PetPooja" && !loadResult.data.agent_code) {
            form.setValue("agent_code", "PP_1", { shouldValidate: true });
          }
        }
      })
      .catch((err) => console.error("Fetch error:", err))
      .finally(() => setLoading(false));
  }, [navigate, form, setLoading]);

  return (
    <div className="min-h-screen bg-intellend-white overflow-x-hidden">
      <JourneyHeader />

      <main className="container mx-auto px-3 py-4 max-w-2xl">
        <div className="bg-white shadow-md rounded-lg p-4 mb-4 overflow-hidden">
          <h2 className="text-xl font-bold text-intellend-green mb-4">Business Information</h2>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <fieldset className="border border-gray-300 p-4 rounded-md space-y-4 min-w-0">
                <legend className="font-bold">Business Details</legend>

                {/* Business Name */}
                <FormField
                  control={form.control}
                  name="business_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm">Name of Business<span className="text-red-500">*</span></FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Business name"
                          {...field}
                          className="h-9"
                          disabled={!isBusinessNameEditable}
                          showSuccess={!!field.value && !form.formState.errors.business_name}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Role + GST */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="business_role"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm">Your Role<span className="text-red-500">*</span></FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || ""}>
                          <FormControl>
                            <SelectTrigger className="h-9" showSuccess={!!field.value && !form.formState.errors.business_role}>
                              <SelectValue placeholder="Select role" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="proprietor">Proprietor</SelectItem>
                            <SelectItem value="director">Director</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="other_role"
                    render={({ field }) => (
                      <FormItem className={business_role !== "other" ? "hidden" : ""}>
                        <FormLabel className="text-sm">Specify Role<span className="text-red-500">*</span></FormLabel>
                        <FormControl>
                          <Input placeholder="Your role" {...field} className="h-9" showSuccess={!!field.value && !form.formState.errors.other_role} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="gst_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm">GST Number{MANDATORY_GST_BAPS.includes(sessionBapId || "") && <span className="text-red-500">*</span>}</FormLabel>
                        <FormControl>
                          <Input maxLength={15} placeholder="15-digit GSTIN" {...field} className="uppercase h-9" showSuccess={!!field.value && !form.formState.errors.gst_number} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />


                </div>

                {/* Vintage & Premise Ownership */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="business_vintage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm">Business Vintage<span className="text-red-500">*</span></FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || ""}>
                          <FormControl>
                            <SelectTrigger className="h-9" showSuccess={!!field.value && !form.formState.errors.business_vintage}>
                              <SelectValue placeholder="Select vintage" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="<6 months">0 to 6 Months</SelectItem>
                            <SelectItem value="6mths to 1yr">6 Months to 1 Year</SelectItem>
                            <SelectItem value="1 to 3yrs">1 to 3 Years</SelectItem>
                            <SelectItem value="3+">3+</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="business_premise_ownership"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm">Business Premises<span className="text-red-500">*</span></FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || ""}>
                          <FormControl>
                            <SelectTrigger className="h-9" showSuccess={!!field.value && !form.formState.errors.business_premise_ownership}>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="self_owned">Self Owned</SelectItem>
                            <SelectItem value="rented">Rented</SelectItem>
                            <SelectItem value="owned_parental">Owned (Parental)</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Constitution */}
                <FormField
                  control={form.control}
                  name="business_constitution_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm">Constitution<span className="text-red-500">*</span></FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || ""}>
                        <FormControl>
                          <SelectTrigger className="h-9" showSuccess={!!field.value && !form.formState.errors.business_constitution_type}>
                            <SelectValue placeholder="Business type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="soleProprietorship">Sole Proprietorship</SelectItem>
                          <SelectItem value="llp">Limited Liability Partnership (LLP)</SelectItem>
                          <SelectItem value="partnership">Partnership Firm</SelectItem>
                          <SelectItem value="privateLimited">Private Limited Company</SelectItem>
                          <SelectItem value="publicLimited">Public Limited Company</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Sales Agent */}
                {agents.length > 0 && (
                  <FormField
                    control={form.control}
                    name="agent_code"
                    render={({ field }) => (
                      <FormItem className="flex flex-col pt-2.5">
                        <FormLabel className="text-sm">Sales Agent</FormLabel>
                        <Popover open={openAgent} onOpenChange={setOpenAgent}>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                role="combobox"
                                className={cn(
                                  "w-full justify-between h-9 uppercase text-left font-normal",
                                  !field.value && "text-muted-foreground lowercase"
                                )}
                              >
                                {field.value
                                  ? agents.find(
                                      (agent) => agent === field.value
                                    ) || field.value
                                  : "Select sales agent"}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-[300px] sm:w-[350px] p-0" align="start">
                            <Command>
                              <CommandInput placeholder="Search sales agent..." />
                              <CommandList>
                                <CommandEmpty>No sales agent found.</CommandEmpty>
                                <CommandGroup>
                                  {agents.map((agent) => (
                                    <CommandItem
                                      value={agent}
                                      key={agent}
                                      onSelect={() => {
                                        form.setValue("agent_code", agent, { shouldValidate: true, shouldDirty: true });
                                        setOpenAgent(false);
                                      }}
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4",
                                          agent === field.value
                                            ? "opacity-100"
                                            : "opacity-0"
                                        )}
                                      />
                                      {agent}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </fieldset>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsCallbackOpen(true)} className="w-full sm:w-auto h-9">
                  Request Callback
                </Button>
                <Button type="submit" className="w-full sm:w-auto sm:ml-auto bg-intellend-green hover:bg-intellend-green/90 h-9" disabled={loading || !form.formState.isValid}>
                  Next
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </main>

      <CallbackHelp
        isOpen={isCallbackOpen}
        setIsOpen={setIsCallbackOpen}
        showOk={false}
        showCallUs={true}
        showWhatsappUs={true}
        silentlyTrigger={true}
        title="Intellend Support"
        description="Reach out to us using one of the following options"
      />
    </div>
  );
};

export default BusinessInfoWhitespace;
